'use client'

import { LightHeaderNav } from './nav'
import { Logo } from '../logo'
import { ButtonLink } from '../../../sotano-kit/primitives/button'
import { Container } from '~/sotano-kit/primitives/container'
import { UserNav } from './user-nav'
import { MobileMenu } from './mobile'
import { useSelectedLayoutSegments } from 'next/navigation'
import { HeaderMiddleLinksFragment } from './links'
import { 
  ContextMenu, 
  ContextMenuContent, 
  ContextMenuItem, 
  ContextMenuTrigger 
} from '~/sotano-kit/primitives/context-menu'
import { ClipboardCopyIcon } from '@radix-ui/react-icons'
import { toast } from 'sonner'
import React from 'react'

export const LightHeader = ({
  headerLinks,
}: {
  headerLinks: HeaderMiddleLinksFragment[]
}) => {
  const [selectedLayoutSegment] = useSelectedLayoutSegments()
  const isInMktPage = !selectedLayoutSegment
    ? true
    : [
        'manifesto',
        'docs',
        'home',
        'changelog',
        'blog',
        'templates',
        'pricing',
      ].includes(selectedLayoutSegment)

  const logoRef = React.useRef<SVGSVGElement>(null)

  const handleCopySVG = React.useCallback(async (grayscale = false) => {
    try {
      if (logoRef.current) {
        // Get the SVG element and create a copy
        const svgElement = logoRef.current.cloneNode(true) as SVGSVGElement
        
        // Set proper attributes for standalone SVG
        svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
        
        // Define color values
        const colors = grayscale 
          ? {
              accent: 'rgb(80, 80, 80)',
              bright: 'rgb(80, 80, 80)',
              bg: 'transparent',
            }
          : {
              accent: '#ff6c02',
              bright: 'white', 
              bg: 'transparent',
            }

        // Find all elements with CSS variable fills/strokes and replace them
        const elementsWithFill = svgElement.querySelectorAll('[fill*="var(--color"]')
        elementsWithFill.forEach((element) => {
          const fill = element.getAttribute('fill')
          if (fill?.includes('var(--color-accent)')) {
            element.setAttribute('fill', colors.accent)
          } else if (fill?.includes('var(--color-bright)')) {
            element.setAttribute('fill', colors.bright)
          } else if (fill?.includes('var(--color-bg)')) {
            element.setAttribute('fill', colors.bg)
          }
        })

        const elementsWithStroke = svgElement.querySelectorAll('[stroke*="var(--color"]')
        elementsWithStroke.forEach((element) => {
          const stroke = element.getAttribute('stroke')
          if (stroke?.includes('var(--color-accent)')) {
            element.setAttribute('stroke', colors.accent)
          } else if (stroke?.includes('var(--color-bright)')) {
            element.setAttribute('stroke', colors.bright)
          } else if (stroke?.includes('var(--color-bg)')) {
            element.setAttribute('stroke', colors.bg)
          }
        })
        
        // Convert to string
        const svgString = new XMLSerializer().serializeToString(svgElement)
        
        console.log('Final SVG:', svgString.substring(0, 500))
        console.log('Contains var(--color-accent):', svgString.includes('var(--color-accent)'))
        
        // Copy to clipboard
        await navigator.clipboard.writeText(svgString)
        
        toast.success(`SVG${grayscale ? ' (grayscale)' : ''} copied to clipboard`, {
          icon: <ClipboardCopyIcon width={16} height={16} />,
        })
      }
    } catch (error) {
      toast.error('Failed to copy SVG to clipboard')
    }
  }, [])

  return (
    <header className="sticky top-0 z-50 h-12 w-full justify-between bg-dark-base shadow-header">
      <Container
        size="small"
        className="grid grid-cols-2 lg:grid-cols-[250px_1fr_250px] items-center lg:gap-x-3 h-full !px-4 md:!px-8"
      >
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <ButtonLink
              unstyled
              href={isInMktPage ? '/home' : '/'}
              className="pointer-events-auto relative max-w-max cursor-pointer"
            >
              <h2 className="sr-only">BaseHub Logo</h2>

              {/* Logo for tablet & desktop */}
              <Logo ref={logoRef} className="w-18 md:w-[92px]" />
            </ButtonLink>
          </ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem onClick={() => handleCopySVG(false)}>
              <ClipboardCopyIcon className="mr-2 h-4 w-4" />
              Copy as SVG
            </ContextMenuItem>
            <ContextMenuItem onClick={() => handleCopySVG(true)}>
              <ClipboardCopyIcon className="mr-2 h-4 w-4" />
              Copy as SVG (grayscale)
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>

        <div className="hidden lg:flex justify-center h-full">
          <LightHeaderNav headerLinks={headerLinks} />
        </div>

        <div className="flex justify-end">
          <UserNav themeSwitcher={!isInMktPage} />
          <div className="lg:hidden flex justify-end ml-1">
            <MobileMenu headerLinks={headerLinks} />
          </div>
        </div>
      </Container>
    </header>
  )
}
